import { Box, Button, Flex, useDisclosure } from '@chakra-ui/react';
import { debounce, pick } from 'lodash';
import Export from '../export';
import FilterList from '../filter/FilterList';
import FilterButton from '../filter/FilterButton';
import SearchBar from '@components/searchBar';
import { useFilters, initialState } from '@pages/Parahub/hooks/state/useFilters';
import { checkFilterActive, fileDownloader } from '@helpers/utils';
import { useFileUploadPopUp } from '@store/useFileUploadPopUp';
import { UploadIcon2 } from '@assets/icons';
import PostUploadModal from '../PostUploadModal';
import authAxios from '@services/authAxios';
import { useCallback } from 'react';
import { useSnackbarNotification } from '@store/snackbarNotification';

const Head = () => {
    const filters = useFilters();
    const stateUploadfile = useFileUploadPopUp();
    const { onChangeSnackbarNotification } = useSnackbarNotification();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const hasFilters = pick(filters, ['dc_name', 'parahub_name', 'sales_area_name']);
    const showIndicator = checkFilterActive(hasFilters, initialState);

    const debounceSearch = useCallback(
        debounce((e) => filters.set({ searchparam: e.target.value }), 400),
        []
    );

    const handleDownloadTemplate = async () => {
        try {
            await fileDownloader({
                url: `/api/v1/parahub-store-config/export-template/customer-parahub`,
                fileName: 'Template config parahub.xlsx',
            });

            onChangeSnackbarNotification({
                status: 'success',
                description: 'Template config parahub berhasil didownload!',
                duration: 3,
            });
        } catch (error) {
            if (error.response && error.response.data instanceof Blob) {
                const text = await error.response.data.text();
                try {
                    const json = JSON.parse(text);
                    onChangeSnackbarNotification({
                        status: 'error',
                        title: 'Gagal download template!',
                        description: `${json?.message}`,
                        duration: 3,
                    });
                } catch {
                    console.error('Error (not JSON):', text);
                }
            } else {
                onChangeSnackbarNotification({
                    status: 'error',
                    description: 'Template config parahub gagal didownload!',
                    duration: 3,
                });
            }
        }
    };

    const handleDownloadErrorFile = async () => {
        try {
            const response = await authAxios.get(`/api/v1/parahub-store-config/download/failed-data`, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Config parahub [Failed-data].xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            onChangeSnackbarNotification({
                status: 'success',
                description: 'Failed Data config parahub berhasil didownload!',
                duration: 3,
            });
        } catch (err) {
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Failed Data config parahub gagal didownload!',
                duration: 3,
            });
        }
    };

    const uploadHandler = async () => {
        const { files, set } = useFileUploadPopUp.getState();
        if (!files?.[0]) return;

        try {
            set({ isUploading: true });

            const formData = new FormData();
            formData.append('file', files[0]);

            const { data } = await authAxios.post(`/api/v1/parahub-store-config/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    set({ uploadProgress: percent });
                },
            });

            set({
                isUploading: false,
                isFinishUploading: true,
                renderContent: () => (
                    <PostUploadModal
                        fileName={files[0].name}
                        isSuccess
                        modalTitle="Sukses Update Data Parahub"
                        onClose={stateUploadfile.reset}
                        detailMessages={data?.data}
                    />
                ),
            });
        } catch (err) {
            const errorMessages = err?.response?.data?.message;
            set({
                isUploading: false,
                isFinishUploading: true,
                renderContent: () => (
                    <PostUploadModal
                        fileName={files[0].name}
                        modalTitle="Gagal Update Data Parahub"
                        onClose={stateUploadfile.reset}
                        onDownload={handleDownloadErrorFile}
                        detailMessages={errorMessages}
                    />
                ),
            });
        }
    };

    const handleUpload = () => {
        stateUploadfile.set({
            isOpen: true,
            title: 'Upload File - Parahub',
            titleDesc:
                'Download file template di bawah ini, lalu upload file tersebut untuk memperbarui data customer Parahub secara massive.',
            onUploadFile: uploadHandler,
            uploadProgressMessage: 'Sedang memeriksa data produk master ...',
            onDownloadTemplate: handleDownloadTemplate,
        });
    };

    return (
        <Box>
            <Flex gap="24px">
                <SearchBar placeholder="Cari nama consumer promotion" onChange={debounceSearch} />
                <FilterButton
                    isOpen={isOpen}
                    onOpen={onOpen}
                    onClose={onClose}
                    showIndicator={showIndicator}
                    label="Filter"
                />
                <Export />
                <Button ml="auto" onClick={handleUpload}>
                    <UploadIcon2 color="#fff" size={20} />
                    Upload File
                </Button>
            </Flex>

            <FilterList isOpen={isOpen} onClose={onClose} />
        </Box>
    );
};

export default Head;
